import { useState } from 'react';
import visaGuideData from '@data/guides/240-hour-visa.json';

const VisaGuide = () => {
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const regions = ['All', 'North America', 'Europe', 'Asia', 'Oceania', 'South America', 'Middle East', 'Africa'];

  const filteredCountries = visaGuideData.eligibleCountries.filter((country) => {
    const matchesRegion = selectedRegion === 'All' || country.regions.includes(selectedRegion);
    const matchesSearch = searchTerm === '' ||
      country.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRegion && matchesSearch;
  });

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          240-Hour Visa-Free Transit Policy
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          {visaGuideData.summary}
        </p>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-primary-800">
            <strong>Last Updated:</strong> {visaGuideData.lastUpdated}
          </p>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Eligible Countries</h2>
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Countries
              </label>
              <input
                type="text"
                placeholder="Search by name..."
                className="input-field"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Region
              </label>
              <select
                className="input-field"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
              >
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Countries
              </label>
              <div className="text-3xl font-bold text-primary-600">
                {filteredCountries.length}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredCountries.map((country) => (
            <div
              key={country.code}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="text-center">
                <div className="text-2xl mb-2">
                  {country.code === 'US' ? '🇺🇸' :
                   country.code === 'GB' ? '🇬🇧' :
                   country.code === 'CA' ? '🇨🇦' :
                   country.code === 'AU' ? '🇦🇺' :
                   country.code === 'DE' ? '🇩🇪' :
                   country.code === 'FR' ? '🇫🇷' :
                   country.code === 'JP' ? '🇯🇵' :
                   country.code === 'KR' ? '🇰🇷' :
                   country.code === 'SG' ? '🇸🇬' :
                   country.code === 'BR' ? '🇧🇷' :
                   country.code === 'RU' ? '🇷🇺' :
                   country.code === 'ES' ? '🇪🇸' :
                   country.code === 'IT' ? '🇮🇹' :
                   '🌐'}
                </div>
                <div className="font-semibold text-sm">{country.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {country.code}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">How to Book Your Tickets</h2>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-center flex-1">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl font-bold text-white">A</span>
                </div>
                <div className="text-sm font-medium">Country A</div>
                <div className="text-xs text-gray-500">Departure</div>
              </div>
              <div className="flex items-center px-4">
                <div className="w-12 h-1 bg-primary-300"></div>
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="w-12 h-1 bg-primary-300"></div>
              </div>
              <div className="text-center flex-1">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl font-bold text-white">CN</span>
                </div>
                <div className="text-sm font-medium">China</div>
                <div className="text-xs text-gray-500">240h Transit</div>
              </div>
              <div className="flex items-center px-4">
                <div className="w-12 h-1 bg-primary-300"></div>
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="w-12 h-1 bg-primary-300"></div>
              </div>
              <div className="text-center flex-1">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl font-bold text-white">B</span>
                </div>
                <div className="text-sm font-medium">Country B</div>
                <div className="text-xs text-gray-500">Destination</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Example:</strong> New York (USA) → Beijing (China, stay 4 days for medical appointment) → Tokyo (Japan)
            </p>
          </div>

          <div className="mt-6 space-y-4">
            {visaGuideData.bookingLogic.steps.map((step) => (
              <div key={step.step} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{step.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Eligible Cities & Airports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visaGuideData.eligibleCities.map((city) => (
            <div key={city.city} className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-2">{city.city}</h3>
              <ul className="space-y-1">
                {city.airports.map((airport, index) => (
                  <li key={index} className="text-sm text-gray-600 flex items-start">
                    <svg className="w-4 h-4 mr-2 mt-0.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {airport}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Required Documents</h2>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-4">
            {visaGuideData.requirements.map((req, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{req.title}</h3>
                  <p className="text-gray-600 text-sm">{req.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Immigration Process</h2>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            {visaGuideData.customsProcedure.steps.map((step) => (
              <div key={step.step} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center border-2 border-primary-200">
                  <span className="text-sm font-bold text-primary-600">{step.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Restrictions & Important Notes</h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <ul className="space-y-3">
            {visaGuideData.restrictions.map((restriction, index) => (
              <li key={index} className="flex gap-3 text-sm">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{restriction}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {visaGuideData.faq.map((item, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold mb-2">{item.question}</h3>
              <p className="text-gray-600 text-sm">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <div className="bg-primary-600 text-white rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Take advantage of visa-free entry policies to access world-class healthcare in China.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/hospitals"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-100 transition-colors"
            >
              Browse Hospitals
            </a>
            <a
              href="/services"
              className="inline-flex items-center justify-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors"
            >
              View Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisaGuide;
